import QUnitApi from '@/api/QUnit';
import QUnitWidgets from '@/api/QUnitWidgets';
import QUnit from './QUnit';

// QUnitCollection()
/*
   Usage:

   QUnitCollection.explore(new QFilters())
                  .then(collection => console.log(collection))
                  .catch(err => console.error("Couldn't fetch QUnits"))
 */

function deep_copy(val) {
  return JSON.parse(JSON.stringify(val));
}

const PROP_NAMES = ['units', 'page', 'bp_name', 'count', 'labels', 'columns'];

let next_instance_id = 1;

class QUnitCollection {
  /**
   * Inmutable QUnit collection manager.
   **/

  constructor(data) {
    if (data === undefined || data === null)
      throw "No data given to QUnitCollection";

    this.units   = ((data.units||data.results||[])
                    .map(q => (q instanceof QUnit) ? q : new QUnit(q)));
    this.page    = data.page || data.current_page_number || 1;
    this.bp_name = data.bp_name || data.current_business_process_name || "";
    this.length  = this.units.length;
    this.count   = data.count || this.length;
    this.labels  = data.labels || [];

    this.columns = data.columns || (Object.entries(data.labels || {})
                                    .map(([key, value]) => ({
                                      'key': key,
                                      ...value,
                                    })));

    this._generate_instance_id();
    Object.seal(this); // Make me inmutable ^.^
  }

  replace_unit(uid, value) {
    // Replace a qunit id with the given value on a new instance.
    // Returns a new QUnitCollection instance

    let res = {};
    for (const p of PROP_NAMES)
      res[p] = this[p];

    res.units = this.units.map(unit => unit.uid == uid ? value : unit);

    return new QUnitCollection(res);
  }

  filter(ef) {
    /**
     * Filters units with ef (QFilters) parameters.
     * Returns a new QUnitCollection instance with the filtered units
     *
     * Available filters:
     *   - pk
     *   - business_process
     *   - business_process_include
     *   - business_process_exclude
     *   - date_range (WARNING: untested)
     *   - sort_by
     *   - sort_desc
     */

    let units = this.units.filter(u => (
      (!ef.get_filter_value('pk') || ef.get_filter_value('pk') == u.uid)
      && (!ef.get_filter_value('business_process') || ef.get_filter_value('business_process') == u.business_process)
      && (!ef.get_filter_value('date_range') || (ef.get_filter_value('date_range')[0] >= u.current_workflow_limit
                          && ef.get_filter_value('date_range')[1] <= u.current_workflow_limit))
      && (!ef.get_filter_value('business_process_include') || ef.get_filter_value('business_process_include').length <= 0 
                            || ef.get_filter_value('business_process_include').includes(u.bp_reference))
      && (!ef.get_filter_value('business_process_exclude') || ef.get_filter_value('business_process_exclude').length <= 0 
                            || !ef.get_filter_value('business_process_exclude').includes(u.bp_reference))
      && (!ef.get_filter_value('centro_list') || ef.get_filter_value('centro_list').length <= 0 
                            || !ef.get_filter_value('centro_list').includes(u._protected.centro))
    ));

    if (ef.get_filter_value('sort_by')) {
      const reverse = ef.get_filter_value('sort_desc') ? -1 : +1;
      units.sort((a, b) => {
        const ca = a[ef.get_filter_value('sort_by')].comparable();
        const cb = b[ef.get_filter_value('sort_by')].comparable();
        if (!ca && !cb)
          return 0;
        else if (!ca)
          return +1;
        else if (!cb)
          return -1;
        else if (ca < cb)
          return -reverse;
        else if (ca == cb)
          return 0;
        return reverse;
      });
    }

    let res = {
      units: units,
      count: units.length,
      page: ef.get_filter_value('page') || 1,
      bp_name: this.bp_name,
      labels: this.labels,
      columns: this.columns,
    };

    return new QUnitCollection(res);
  }

  overwrite(data) {
    // Overwrite current properties with data and return a new QUnitCollection.
    const merged = {};
    for (const kw of PROP_NAMES) {
      merged[kw] = data[kw] === undefined ? this[kw] : data[kw];
    }

    return new QUnitCollection(merged);
  }

  indexOf(uid) {
    // Returns the first index inside this.units of the matching uid
    // Returns -1 if not found
    for (let i = 0; i < this.units.length; i++) {
      if (this.units[i].uid === uid) {
        return i;
      }
    }
    return -1;
  }

  search(uid) {
    // Returns the QUnit matching uid or null
    const pos = this.indexOf(uid);
    if (pos >= 0) {
      return this.units[pos];
    }
    return null;
  }

  static async explore(explorer_filters, cancel_token = null) {
    return await QUnitCollection._query_endpoint(
      QUnitApi.getExplorer,
      explorer_filters,
      cancel_token
    );
  }

  static async globalSearch(explorer_filters, cancel_token = null) {
    return await QUnitCollection._query_endpoint(
      QUnitApi.globalSearch,
      explorer_filters,
      cancel_token
    );
  }

  static async todoList(explorer_filters, cancel_token = null) {
    return await QUnitCollection._query_endpoint(
      QUnitApi.getTodoList,
      explorer_filters,
      cancel_token
    );
  }

  static async todoCalendar(explorer_filters, cancel_token = null) {
    return await QUnitCollection._query_endpoint(
      QUnitApi.getTodoCalendar,
      explorer_filters,
      cancel_token
    );
  }

  static async exploreCalendar(explorer_filters, cancel_token = null) {
    return await QUnitCollection._query_endpoint(
      QUnitApi.getExplorerCalendar,
      explorer_filters,
      cancel_token
    );
  }

  static async getQUnitRelated(explorer_filters, cancel_token = null) {
    return await QUnitCollection._query_endpoint(
      QUnitApi.getQUnitRelated,
      explorer_filters,
      cancel_token
    );
  }

  static async subindicators(explorer_filters, cancel_token = null) {
    return await QUnitCollection._query_endpoint(
      QUnitWidgets.subindicators,
      explorer_filters,
      cancel_token,
    );
  }

  static async _query_endpoint(endpoint, explorer_filters, cancel_token = null) {
    // endpoint is like QUnitApi.getExplorer
    let data = undefined;
    try {
      data = await endpoint(explorer_filters, (cancel_token || {}));
    } catch (err) {
      console.warn(`Network error. ${endpoint.name}(${explorer_filters}) →`, err);
      throw err;
    }
    return new QUnitCollection(data);
  }

  toJSON() {
    const dict = {};
    for (const kw of PROP_NAMES) {
      dict[kw] = this[kw];
    }
    return dict;
  }

  _generate_instance_id() {
    // Generate a unique identifier
    this.instance_id = next_instance_id++; // Vue reactive winks
  }
}

export default QUnitCollection;
